import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Twitter, Facebook, Instagram, Share2, Copy } from "lucide-react";
import { 
  shareToTwitter, 
  shareToFacebook, 
  shareNative, 
  copyToClipboard, 
  getShareMessage, 
  ShareType 
} from "@/lib/socialShare";
import { Capacitor } from "@capacitor/core";

interface ShareButtonsProps {
  type: ShareType;
}

const ShareButtons = ({ type }: ShareButtonsProps) => {
  const { toast } = useToast();
  const message = getShareMessage(type);
  const isNative = Capacitor.isNativePlatform();

  const handleNativeShare = async () => {
    const success = await shareNative(message);
    if (!success) {
      toast({ 
        title: "Sharing not supported", 
        description: "Please use the platform buttons below" 
      });
    }
  };

  const handleInstagramShare = async () => {
    await copyToClipboard(message);
    toast({
      title: "Copied to clipboard!",
      description: "Open Instagram and paste in your story or post",
    });
  };

  const handleCopyLink = async () => {
    await copyToClipboard(message);
    toast({ title: "Copied!", description: "Share message copied to clipboard" });
  };

  if (isNative) {
    return (
      <Button onClick={handleNativeShare} className="w-full" variant="outline">
        <Share2 className="w-5 h-5 mr-2" />
        Share on Social Media
      </Button>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground text-center">Share your moment</p>
      <div className="flex justify-center gap-3">
        <Button 
          size="icon" 
          variant="outline" 
          onClick={() => shareToTwitter(message)} 
          title="Share on X"
        >
          <Twitter className="w-5 h-5" />
        </Button>
        <Button 
          size="icon" 
          variant="outline" 
          onClick={() => shareToFacebook(message)} 
          title="Share on Facebook"
        >
          <Facebook className="w-5 h-5" />
        </Button>
        <Button 
          size="icon" 
          variant="outline" 
          onClick={handleInstagramShare} 
          title="Share on Instagram"
        >
          <Instagram className="w-5 h-5" />
        </Button>
        <Button 
          size="icon" 
          variant="outline" 
          onClick={handleCopyLink} 
          title="Copy to clipboard"
        >
          <Copy className="w-5 h-5" />
        </Button>
      </div>
    </div>
  );
};

export default ShareButtons;
